import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { useJointBillServicePeriod } from "@pages/BillsPage/useJointBillServicePeriod";
import { usePreviousPeriodData } from "@pages/BillsPage/usePreviousPeriodData";
import { type TDropdownOption } from "@components/Dropdown/Dropdown";
import { JointBillFilters } from "@components/JointBillFilters/JointBillFilters";
import { useJointBillServicePeriodFilters } from "@hooks/useJointBillServicePeriodList";
import { useValidJointBillServicesForPeriod } from "@hooks/useValidJointBillServicesForPeriod";
import { useVisibleJointBillServiceIds } from "@hooks/useVisibleJointBillServiceIds";
import { useOverviewQuery } from "@services/overview/queries";
import { isNullOrUndefined } from "@utils/common";

interface IJointBillListFiltersProps {
  path?: string;
}

export interface IJointBillListPeriodOption
  extends TDropdownOption<string, number> {
  year: number;
  month: number;
}

export const JointBillListFilters: React.FC<IJointBillListFiltersProps> = ({
  path,
}) => {
  const { t } = useTranslation();
  const { periodId = 0 } = useJointBillServicePeriod();
  const visibleJbsIds = useVisibleJointBillServiceIds();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { shouldDisplayUnpaidWarning, previousPeriodId } =
    usePreviousPeriodData();
  const jointBillServiceIdFilter = searchParams.getAll("jbsId");
  const filteredJointBillServiceId =
    jointBillServiceIdFilter.length === 1
      ? parseInt(jointBillServiceIdFilter[0] ?? "0")
      : 0;
  const validJbsForPeriod = useValidJointBillServicesForPeriod(periodId);
  const { data: overview } = useOverviewQuery();
  const jointBillServices = (overview?.jointBillServices ?? [])
    ?.filter((jbs) => validJbsForPeriod.includes(jbs.jointBillServiceId))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const periodFilters = useJointBillServicePeriodFilters(visibleJbsIds);

  const periodOptions: IJointBillListPeriodOption[] = periodFilters.map(
    (p) => ({
      label: p.label,
      value: p.periodId,
      year: p.year,
      month: p.month,
      href: `${path}/${p.periodId}${
        !isNullOrUndefined(jointBillServiceIdFilter) &&
        jointBillServiceIdFilter.length === 1
          ? `?jbsId=${jointBillServiceIdFilter[0]}`
          : ""
      }`,
      isError: p.periodId === previousPeriodId && shouldDisplayUnpaidWarning,
    }),
  );

  const jointBillServiceOptions = [
    {
      label: t("jointBillListFilters.filters.house.defaultLabel"),
      value: 0,
      href: location.pathname,
    },
    ...jointBillServices.map((jbs) => ({
      label: jbs.displayName ?? t("common.defaultAddress"),
      value: jbs.jointBillServiceId,
      href: `${location.pathname}?jbsId=${jbs.jointBillServiceId}`,
    })),
  ];

  return (
    <JointBillFilters
      jointBillServiceOptions={jointBillServiceOptions}
      jointBillServiceValue={filteredJointBillServiceId}
      periodOptions={periodOptions}
      periodValue={periodId}
      periodError={
        shouldDisplayUnpaidWarning
          ? t("jointBillListFilters.errors.unpaidPreviousPeriod")
          : undefined
      }
      isPlacedInHeader={true}
    />
  );
};
